// Menu Consultas (pedido 2026-10-04) — antecedentes criminais e CPF pela
// API Full (https://doc.apifull.com.br). Token da conta API Full é POR
// EMPRESA e, desde 2026-10-09, só o Super Master configura (Integrações →
// Cobrança & Consultas); pra empresa a fonte se chama "GPS Drive API" e o
// nome do fornecedor nunca aparece. Cada
// consulta gasta crédito e fica no histórico — reabrir não cobra de novo.
//
// Redesenho "Consulta de Antecedentes" (2026-10-04): visual de central de
// inteligência, tema escuro próprio (não segue o tema do app). Só CPF tem
// fonte autorizada integrada — as outras abas de busca aparecem como "não
// integrado" e nunca simulam resultado. Finalidade é obrigatória e fica
// gravada no histórico (auditoria LGPD). "Ver demonstração" mostra um
// perfil 100% fictício só para conhecer o layout.
import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  Car,
  CheckCircle2,
  ChevronDown,
  Clock,
  Database,
  ExternalLink,
  Eye,
  FileText,
  Gavel,
  Home,
  IdCard,
  Loader2,
  Lock,
  Mail,
  MapPin,
  Navigation,
  Network,
  Landmark,
  Phone,
  QrCode,
  Search,
  ShieldAlert,
  ShieldCheck,
  User,
  Wallet,
  UserSearch,
  Users,
  X,
  type LucideIcon,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { ListPagination } from "@/components/list-pagination";
import {
  useConsultationAccess,
  useConsultationCredits,
  useConsultationHistory,
  useConsultationSettings,
} from "@/hooks/queries/consultations";
import {
  consultationsService,
  type Consultation,
  type ConsultationType,
} from "@/services/gpsdrive-consultations.service";
import { resolveGpsDriveUrl } from "@/lib/api/gpsdrive-client";
import { BrazilMap, Fingerprint } from "@/components/consultas/visuals";
import { ConsultationPrintReport } from "@/components/consultas/consultation-report";
import { LegalChecksPanel, legalOf } from "@/components/consultas/legal-checks";
import {
  PublicDataPanel,
  PublicDataSummary,
  publicDataCount,
  publicDataOf,
} from "@/components/consultas/public-data-panel";
import {
  DEMO_PROFILE,
  type Profile,
  type ProfileList,
} from "@/components/consultas/profile";
import { LicensePlate } from "@/components/license-plate";
import {
  PURPOSES,
  PixCreditDialog,
  consultationProfile,
  errMsg,
  extractData,
  fmtBrl,
  maskCpf,
  runErrMsg,
  validCpf,
} from "@/components/consultas/shared";

export const Route = createFileRoute("/consultas")({
  head: () => ({ meta: [{ title: "Consulta de Antecedentes" }] }),
  component: ConsultasGate,
});

const TYPES: {
  key: ConsultationType;
  label: string;
  hint: string;
  icon: LucideIcon;
  pdf: boolean;
}[] = [
  // Tudo numa consulta só (2026-10-12). Os outros 3 ficam só pra mostrar o
  // nome nas consultas antigas do histórico.
  {
    key: "completa",
    label: "Consulta completa",
    hint: "Antecedentes + CPF Ultra completo + processos + dados públicos",
    icon: ShieldCheck,
    pdf: false,
  },
  {
    key: "antecedentes",
    label: "Antecedentes criminais",
    hint: "Certidão federal (PF)",
    icon: ShieldAlert,
    pdf: true,
  },
  {
    key: "cpf_simples",
    label: "CPF simples",
    hint: "Dados básicos do titular",
    icon: UserSearch,
    pdf: true,
  },
  {
    key: "cpf_completo",
    label: "CPF completo",
    hint: "Cadastro completo",
    icon: IdCard,
    pdf: false,
  },
];
const typeLabel = (t: string) => TYPES.find((x) => x.key === t)?.label ?? t;

// Abas de busca. Só CPF tem fonte autorizada integrada hoje.
type SearchKind = "cpf" | "nome" | "rg" | "cnh" | "telefone" | "email" | "endereco";
const SEARCH_TABS: { key: SearchKind; label: string; placeholder: string; icon: LucideIcon }[] = [
  { key: "cpf", label: "CPF", placeholder: "000.000.000-00", icon: IdCard },
  { key: "nome", label: "Nome", placeholder: "Nome completo", icon: User },
  { key: "rg", label: "RG", placeholder: "00.000.000-0", icon: IdCard },
  { key: "cnh", label: "CNH", placeholder: "00000000000", icon: Car },
  { key: "telefone", label: "Telefone", placeholder: "(00) 00000-0000", icon: Phone },
  { key: "email", label: "E-mail", placeholder: "nome@dominio.com", icon: Mail },
  { key: "endereco", label: "Endereço", placeholder: "Rua, número, cidade/UF", icon: Home },
];

type ResultTab =
  | "geral"
  | "enderecos"
  | "antecedentes"
  | "processos"
  | "veiculos"
  | "telefones"
  | "emails"
  | "vinculos"
  | "publicos";

const fmtDate = (iso: string) =>
  new Date(iso).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" });

// Estilos próprios da tela (tema "central de inteligência").
const CX_CSS = `
.cx-root{--cx-bg:#030712;--cx-panel:rgba(8,20,38,.72);--cx-border:rgba(52,211,153,.18);--cx-neon:#34d399;--cx-cyan:#22d3ee;--cx-text:#e2e8f0;--cx-muted:#7b8ba3;
  background:radial-gradient(1200px 600px at 15% -10%,rgba(16,185,129,.12),transparent 60%),radial-gradient(900px 500px at 100% 0%,rgba(34,211,238,.10),transparent 60%),linear-gradient(180deg,#030712 0%,#05101f 50%,#030712 100%);
  color:var(--cx-text);position:relative;isolation:isolate}
.cx-root::before{content:"";position:absolute;inset:0;z-index:-1;pointer-events:none;
  background-image:linear-gradient(rgba(34,211,238,.045) 1px,transparent 1px),linear-gradient(90deg,rgba(34,211,238,.045) 1px,transparent 1px);background-size:44px 44px;
  mask-image:linear-gradient(180deg,#000 0%,rgba(0,0,0,.35) 60%,transparent 100%)}
.cx-card{background:linear-gradient(180deg,rgba(10,26,48,.78),rgba(5,14,28,.82));border:1px solid var(--cx-border);border-radius:14px;
  box-shadow:0 0 0 1px rgba(255,255,255,.015) inset,0 18px 40px -24px rgba(0,0,0,.9),0 0 24px -16px rgba(52,211,153,.45);backdrop-filter:blur(8px);position:relative}
.cx-card::after{content:"";position:absolute;left:18px;right:18px;top:-1px;height:1px;background:linear-gradient(90deg,transparent,rgba(52,211,153,.6),transparent);pointer-events:none}
.cx-title{font-size:11px;font-weight:700;letter-spacing:.16em;text-transform:uppercase;color:#a7f3d0;display:flex;align-items:center;gap:8px}
.cx-label{font-size:10px;font-weight:600;letter-spacing:.14em;text-transform:uppercase;color:var(--cx-muted)}
.cx-mono{font-family:ui-monospace,SFMono-Regular,Menlo,Consolas,monospace}
.cx-input{background:rgba(2,8,20,.85);border:1px solid rgba(52,211,153,.22);color:var(--cx-text);border-radius:10px;outline:none;transition:border-color .2s,box-shadow .2s}
.cx-input::placeholder{color:#475569}
.cx-input:focus{border-color:var(--cx-neon);box-shadow:0 0 0 3px rgba(52,211,153,.15),0 0 18px -4px rgba(52,211,153,.6)}
.cx-input:disabled{opacity:.45;cursor:not-allowed}
.cx-btn{background:linear-gradient(180deg,#4ade80,#10b981);color:#022c22;font-weight:800;letter-spacing:.14em;border-radius:10px;
  box-shadow:0 0 0 1px rgba(110,231,183,.5) inset,0 0 22px -2px rgba(52,211,153,.65);transition:transform .15s,box-shadow .2s,filter .2s}
.cx-btn:hover:not(:disabled){filter:brightness(1.08);box-shadow:0 0 0 1px rgba(167,243,208,.7) inset,0 0 32px 0 rgba(52,211,153,.75)}
.cx-btn:active:not(:disabled){transform:translateY(1px)}
.cx-btn:disabled{filter:grayscale(.8) brightness(.55);box-shadow:none;cursor:not-allowed}
.cx-ghost{border:1px solid rgba(52,211,153,.25);color:#a7f3d0;border-radius:10px;background:rgba(16,185,129,.06);transition:background .2s,border-color .2s}
.cx-ghost:hover{background:rgba(16,185,129,.14);border-color:rgba(52,211,153,.5)}
.cx-tab{position:relative;color:var(--cx-muted);transition:color .2s}
.cx-tab:hover{color:#cbd5e1}
.cx-tab[data-active=true]{color:#6ee7b7}
.cx-tab[data-active=true]::after{content:"";position:absolute;left:10%;right:10%;bottom:-1px;height:2px;border-radius:2px;background:var(--cx-neon);box-shadow:0 0 10px var(--cx-neon)}
.cx-chip{border:1px solid rgba(148,163,184,.16);background:rgba(15,23,42,.5);border-radius:12px;transition:all .2s}
.cx-chip:hover{border-color:rgba(52,211,153,.35)}
.cx-chip[data-active=true]{border-color:rgba(52,211,153,.7);background:rgba(16,185,129,.1);box-shadow:0 0 18px -6px rgba(52,211,153,.7)}
.cx-dot{width:7px;height:7px;border-radius:99px;display:inline-block}
.cx-dot-on{background:#34d399;box-shadow:0 0 8px #34d399}
.cx-dot-off{background:#475569}
.cx-dot-warn{background:#f59e0b;box-shadow:0 0 8px #f59e0b}
.cx-row{border:1px solid rgba(148,163,184,.1);background:rgba(2,8,20,.45);border-radius:10px;transition:border-color .2s,background .2s}
.cx-row:hover{border-color:rgba(52,211,153,.3);background:rgba(16,185,129,.05)}
.cx-fade{animation:cx-fade .5s cubic-bezier(.2,.7,.2,1) both}
@keyframes cx-fade{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:none}}
.cx-ping{transform-box:fill-box;transform-origin:center;animation:cx-ping 3s ease-out infinite}
@keyframes cx-ping{0%{transform:scale(.4);opacity:.6}80%,100%{transform:scale(2.2);opacity:0}}
.cx-dash{stroke-dasharray:3 4;animation:cx-dash 2.4s linear infinite}
@keyframes cx-dash{to{stroke-dashoffset:-28}}
.cx-scanline{animation:cx-scan 2.8s ease-in-out infinite}
@keyframes cx-scan{0%{transform:translateY(-10px)}50%{transform:translateY(96px)}100%{transform:translateY(-10px)}}
.cx-bar{animation:cx-bar 1.4s ease-in-out infinite}
@keyframes cx-bar{0%,100%{transform:scaleY(.3);opacity:.4}50%{transform:scaleY(1);opacity:1}}
.cx-radar{background:conic-gradient(from 0deg,rgba(52,211,153,.35),transparent 30%);animation:cx-spin 2s linear infinite}
@keyframes cx-spin{to{transform:rotate(360deg)}}
.cx-map iframe{filter:invert(92%) hue-rotate(180deg) saturate(.55) brightness(.85) contrast(1.05)}
.cx-scroll::-webkit-scrollbar{height:6px;width:6px}.cx-scroll::-webkit-scrollbar-thumb{background:rgba(52,211,153,.25);border-radius:9px}
.cx-pager>div{border-color:rgba(52,211,153,.12);color:#7b8ba3}
.cx-pager select{background:#020814;color:#e2e8f0;border-color:rgba(52,211,153,.25)}
.cx-pager button.border{background:rgba(2,8,20,.6);color:#cbd5e1;border-color:rgba(52,211,153,.2)}
.cx-pager button:not(.border){background:#10b981;color:#022c22;font-weight:700}
@media (prefers-reduced-motion:reduce){.cx-root *{animation:none!important}}
`;

function Shell({ children }: { children: ReactNode }) {
  return (
    <div className="cx-root min-h-full">
      <style>{CX_CSS}</style>
      {children}
    </div>
  );
}

function ConsultasGate() {
  const { data: access, isLoading } = useConsultationAccess();
  if (isLoading) {
    return (
      <Shell>
        <div className="flex h-screen items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-emerald-400" />
        </div>
      </Shell>
    );
  }
  if (!access?.view) {
    return (
      <Shell>
        <div className="flex h-screen items-center justify-center p-6">
          <div className="cx-card max-w-md p-8 text-center">
            <Lock className="mx-auto h-8 w-8 text-emerald-400" />
            <h1 className="mt-3 text-lg font-semibold">Sem acesso a Consultas</h1>
            <p className="mt-2 text-sm text-slate-400">
              Peça a um administrador para liberar em Usuários → Editar → Permissões → Consultas.
            </p>
          </div>
        </div>
      </Shell>
    );
  }
  return <ConsultasPage canConfigure={access.configure} />;
}

type View =
  | { kind: "idle" }
  | { kind: "loading" }
  | { kind: "error"; message: string }
  | { kind: "result"; consultation: Consultation }
  | { kind: "demo" };

function ConsultasPage({ canConfigure }: { canConfigure: boolean }) {
  const qc = useQueryClient();
  const { data: settings, isLoading: settingsLoading } = useConsultationSettings();
  const [kind, setKind] = useState<SearchKind>("cpf");
  const type: ConsultationType = "completa";
  const [query, setQuery] = useState("");
  const [pdf, setPdf] = useState(true);
  const [purpose, setPurpose] = useState(PURPOSES[0]);
  const [purposeOther, setPurposeOther] = useState("");
  const [agree, setAgree] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [view, setView] = useState<View>({ kind: "idle" });
  const [pixOpen, setPixOpen] = useState(false);
  // Toda consulta é paga (R$ 10,00 por PIX desde 2026-10-12) — cliente pra empresa,
  // admin/funcionário pra plataforma (2026-10-09).
  const { data: credits } = useConsultationCredits(true, pixOpen);
  const paid = !!credits?.required;
  const noCredit = paid && (credits?.balance ?? 0) < (credits?.price ?? 10);
  const resultsRef = useRef<HTMLDivElement>(null);
  const def = TYPES.find((t) => t.key === type)!;
  const hasToken = !!settings?.hasToken;
  const integrated = kind === "cpf";
  const running = view.kind === "loading";

  const scrollToResults = () =>
    setTimeout(
      () => resultsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }),
      50,
    );

  const run = async () => {
    if (!integrated) return;
    if (!validCpf(query)) return setFormError("CPF inválido.");
    const finalPurpose = purpose === "Outra" ? purposeOther.trim() : purpose;
    if (finalPurpose.length < 3) return setFormError("Descreva a finalidade da consulta.");
    if (!agree) return setFormError("Confirme a base legal e a finalidade antes de consultar.");
    setFormError(null);
    if (noCredit) return setPixOpen(true);
    setView({ kind: "loading" });
    scrollToResults();
    try {
      const r = await consultationsService.run({
        type,
        cpf: query,
        purpose: finalPurpose,
        ...(def.pdf && pdf ? { pdf: true } : {}),
      });
      setView({ kind: "result", consultation: r });
      qc.invalidateQueries({ queryKey: ["consultations", "list"] });
      qc.invalidateQueries({ queryKey: ["consultations", "settings"] });
      qc.invalidateQueries({ queryKey: ["consultations", "credits"] });
    } catch (err) {
      setView({ kind: "error", message: runErrMsg(err) });
      qc.invalidateQueries({ queryKey: ["consultations", "credits"] });
      qc.invalidateQueries({ queryKey: ["consultations", "list"] });
      setTimeout(() => qc.invalidateQueries({ queryKey: ["consultations", "list"] }), 45_000);
    }
  };

  const openFromHistory = async (id: string) => {
    setView({ kind: "loading" });
    scrollToResults();
    try {
      const c = await qc.fetchQuery({
        queryKey: ["consultations", "one", id],
        queryFn: () => consultationsService.get(id),
      });
      setView({ kind: "result", consultation: c });
    } catch (err) {
      setView({ kind: "error", message: errMsg(err) });
    }
  };

  return (
    <Shell>
      <div className="mx-auto max-w-[1400px] space-y-5 px-4 py-5 md:px-6 md:py-6">
        <Hero settings={settings} settingsLoading={settingsLoading} />

        {!settingsLoading && !hasToken && (
          <div className="cx-fade flex items-start gap-2 rounded-xl border border-amber-400/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-200">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            {canConfigure
              ? "A GPS Drive API ainda não foi ativada para a sua empresa. Fale com o suporte."
              : "A GPS Drive API ainda não foi ativada para a sua empresa. Peça ao administrador."}
          </div>
        )}

        {paid && credits && (
          <div className="cx-card cx-fade flex flex-col gap-3 p-4 sm:flex-row sm:items-center">
            <span className="grid h-10 w-10 shrink-0 place-items-center rounded-lg border border-emerald-400/40 bg-emerald-400/10 text-emerald-300">
              <Wallet className="h-5 w-5" />
            </span>
            <div className="min-w-0 flex-1">
              <div className="cx-label">Seu saldo para consultas</div>
              <div className="text-lg font-bold text-slate-100">
                {fmtBrl(credits.balance)}{" "}
                <span className="text-xs font-normal text-slate-400">
                  · {Math.floor(credits.balance / credits.price)} consulta(s) · cada uma custa{" "}
                  {fmtBrl(credits.price)}
                </span>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setPixOpen(true)}
              className="cx-ghost flex h-10 items-center justify-center gap-2 px-4 text-xs font-bold uppercase tracking-[0.12em]"
            >
              <QrCode className="h-4 w-4" />
              Adicionar crédito (PIX)
            </button>
          </div>
        )}

        {/* Área de consulta */}
        <section className="cx-card cx-fade p-4 md:p-5" style={{ animationDelay: "80ms" }}>
          <div className="cx-scroll -mx-1 flex gap-1 overflow-x-auto border-b border-emerald-400/10 px-1">
            {SEARCH_TABS.map((t) => (
              <button
                key={t.key}
                type="button"
                data-active={kind === t.key}
                onClick={() => {
                  setKind(t.key);
                  setQuery("");
                  setFormError(null);
                }}
                className="cx-tab flex shrink-0 items-center gap-1.5 px-3.5 py-2.5 text-xs font-bold uppercase tracking-[0.14em]"
              >
                <t.icon className="h-3.5 w-3.5" />
                {t.label}
              </button>
            ))}
          </div>

          {integrated && (
            <div className="cx-chip mt-4 flex items-start gap-3 p-3" data-active="true">
              <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg border border-emerald-400/60 bg-emerald-400/15 text-emerald-300">
                <def.icon className="h-4 w-4" />
              </span>
              <span className="min-w-0">
                <span className="block text-sm font-semibold text-slate-100">
                  {def.label}
                  {credits?.price != null && (
                    <span className="ml-2 text-emerald-300">{fmtBrl(credits.price)}</span>
                  )}
                </span>
                <span className="block text-xs text-slate-400">{def.hint}</span>
                <span className="mt-1.5 block text-xs text-amber-200/90">
                  Agora é tudo numa consulta só: antes eram Antecedentes criminais (R$ 5,00) + CPF
                  completo (R$ 5,00) separados — pelo mesmo valor, você recebe os dois juntos e
                  ainda processos, mandados, protestos, dívida ativa e dados públicos.
                </span>
              </span>
            </div>
          )}

          <div className="mt-4 flex flex-col gap-3 lg:flex-row lg:items-end">
            <label className="block flex-1">
              <span className="cx-label mb-1.5 block">
                {SEARCH_TABS.find((t) => t.key === kind)!.label}
              </span>
              <div className="relative">
                <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-emerald-400/70" />
                <input
                  className="cx-input cx-mono h-12 w-full pl-10 pr-3 text-base tracking-wider"
                  inputMode={kind === "cpf" ? "numeric" : "text"}
                  placeholder={SEARCH_TABS.find((t) => t.key === kind)!.placeholder}
                  value={query}
                  disabled={!integrated}
                  onChange={(e) =>
                    setQuery(kind === "cpf" ? maskCpf(e.target.value) : e.target.value)
                  }
                  onKeyDown={(e) => e.key === "Enter" && !running && hasToken && run()}
                />
              </div>
            </label>
            {integrated && (
              <label className="block lg:w-[300px]">
                <span className="cx-label mb-1.5 block">Finalidade</span>
                <select
                  value={purpose}
                  onChange={(e) => setPurpose(e.target.value)}
                  className="cx-input h-12 w-full px-3 text-sm"
                >
                  {PURPOSES.map((p) => (
                    <option key={p} value={p} className="bg-slate-900">
                      {p}
                    </option>
                  ))}
                </select>
              </label>
            )}
            <button
              type="button"
              onClick={run}
              disabled={!integrated || running || !hasToken}
              className="cx-btn flex h-12 shrink-0 items-center justify-center gap-2 px-8 text-sm"
            >
              {running ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Search className="h-4 w-4" />
              )}
              {running
                ? "CONSULTANDO…"
                : noCredit
                  ? `PAGAR ${fmtBrl(credits?.price ?? 10)} E CONSULTAR`
                  : "CONSULTAR"}
            </button>
          </div>

          {integrated ? (
            <div className="mt-3 flex flex-col gap-2 text-xs text-slate-400 md:flex-row md:flex-wrap md:items-center md:gap-x-5">
              {purpose === "Outra" && (
                <input
                  className="cx-input h-9 w-full px-3 text-sm md:max-w-sm"
                  placeholder="Descreva a finalidade"
                  value={purposeOther}
                  onChange={(e) => setPurposeOther(e.target.value)}
                />
              )}
              {def.pdf && (
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    className="accent-emerald-400"
                    checked={pdf}
                    onChange={(e) => setPdf(e.target.checked)}
                  />
                  Gerar relatório em PDF
                </label>
              )}
              <label className="flex items-start gap-2">
                <input
                  type="checkbox"
                  className="mt-0.5 accent-emerald-400"
                  checked={agree}
                  onChange={(e) => setAgree(e.target.checked)}
                />
                <span>
                  Declaro que tenho base legal (LGPD) para esta consulta e que ela será usada só
                  para a finalidade informada.
                </span>
              </label>
              <span className="flex items-center gap-1.5 text-slate-500 md:ml-auto">
                <ShieldCheck className="h-3.5 w-3.5 text-emerald-400/70" /> Usuário, data, hora e
                finalidade ficam registrados.
              </span>
            </div>
          ) : (
            <div className="mt-3 flex items-start gap-2 rounded-lg border border-slate-600/30 bg-slate-800/30 px-3 py-2.5 text-xs text-slate-400">
              <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-500" />
              Nenhuma fonte oficial ou provedor autorizado está integrado para busca por{" "}
              {SEARCH_TABS.find((t) => t.key === kind)!.label}. Hoje as consultas são feitas por
              CPF.
            </div>
          )}
          {formError && (
            <div className="mt-3 rounded-lg border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-sm text-rose-300">
              {formError}
            </div>
          )}
        </section>

        <div ref={resultsRef} className="scroll-mt-4">
          {view.kind === "idle" && <IdleState onDemo={() => setView({ kind: "demo" })} />}
          {view.kind === "loading" && <LoadingState />}
          {view.kind === "error" && (
            <ErrorState message={view.message} onClose={() => setView({ kind: "idle" })} />
          )}
          {view.kind === "result" && (
            <Results consultation={view.consultation} onClose={() => setView({ kind: "idle" })} />
          )}
          {view.kind === "demo" && <Results demo onClose={() => setView({ kind: "idle" })} />}
        </div>

        <History onOpen={openFromHistory} />
      </div>
      {paid && credits && (
        <PixCreditDialog
          open={pixOpen}
          balance={credits.balance}
          price={credits.price}
          needsBillingDocument={!!credits.needsBillingDocument}
          onClose={() => setPixOpen(false)}
        />
      )}
    </Shell>
  );
}

// ---------------------------------------------------------------------------
// Cabeçalho: digital + título, mapa do Brasil e fontes integradas.

function Hero({
  settings,
  settingsLoading,
}: {
  settings: { hasToken: boolean; balance: unknown; balanceError: string | null } | undefined;
  settingsLoading: boolean;
}) {
  const on = !!settings?.hasToken;
  const sources: { name: string; detail: string; state: "on" | "off" | "warn" }[] = [
    // Nome do fornecedor nunca aparece pra empresa (pedido 2026-10-09).
    {
      name: "GPS Drive API",
      detail: settingsLoading
        ? "Verificando…"
        : on
          ? settings?.balanceError
            ? "Conectado · com falha"
            : "Conectado"
          : "Não ativada",
      state: settingsLoading ? "warn" : on ? (settings?.balanceError ? "warn" : "on") : "off",
    },
    {
      name: "Antecedentes criminais (PF)",
      detail: on ? "Disponível via GPS Drive API" : "Indisponível",
      state: on ? "on" : "off",
    },
    {
      name: "CPF – dados básicos",
      detail: on ? "Disponível via GPS Drive API" : "Indisponível",
      state: on ? "on" : "off",
    },
    {
      name: "CPF – cadastro completo",
      detail: on ? "Disponível via GPS Drive API" : "Indisponível",
      state: on ? "on" : "off",
    },
    {
      name: "Nome · RG · CNH · Telefone · E-mail · Endereço",
      detail: "Não integrado",
      state: "off",
    },
  ];
  return (
    <section className="cx-card cx-fade overflow-hidden">
      <div className="grid gap-0 lg:grid-cols-12">
        <div className="relative flex flex-col justify-center gap-5 p-5 md:p-7 lg:col-span-5">
          <div className="flex items-center gap-4">
            <div className="relative grid h-20 w-20 shrink-0 place-items-center rounded-2xl border border-emerald-400/30 bg-emerald-400/5 shadow-[0_0_30px_-8px_rgba(52,211,153,.7)] md:h-24 md:w-24">
              <Fingerprint className="h-14 w-14 md:h-16 md:w-16" />
              <span className="absolute -left-px -top-px h-3 w-3 rounded-tl-2xl border-l-2 border-t-2 border-emerald-300" />
              <span className="absolute -bottom-px -right-px h-3 w-3 rounded-br-2xl border-b-2 border-r-2 border-emerald-300" />
            </div>
            <div className="min-w-0">
              <div className="cx-label flex items-center gap-2 text-emerald-300/80">
                <span className="cx-dot cx-dot-on" /> Central de inteligência
              </div>
              <h1 className="mt-1 text-2xl font-extrabold uppercase leading-tight tracking-[0.06em] text-white md:text-[32px]">
                Consulta de{" "}
                <span className="bg-gradient-to-r from-emerald-300 to-cyan-300 bg-clip-text text-transparent">
                  Antecedentes
                </span>
              </h1>
            </div>
          </div>
          <p className="max-w-md text-sm leading-relaxed text-slate-400">
            Acesse informações integradas de pessoas, endereços, vínculos e registros públicos.
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <span className="flex items-center gap-1.5 text-[11px] text-slate-500">
              <ShieldCheck className="h-3.5 w-3.5 text-emerald-400/70" /> LGPD · acesso controlado ·
              auditoria
            </span>
          </div>
        </div>

        <div className="relative hidden min-h-[260px] items-center justify-center border-emerald-400/10 md:flex lg:col-span-4 lg:border-l">
          <BrazilMap className="h-[260px] w-auto max-w-full drop-shadow-[0_0_24px_rgba(34,211,238,.25)]" />
          <div className="cx-mono absolute bottom-3 left-4 text-[10px] uppercase tracking-[0.2em] text-cyan-300/50">
            Cobertura nacional
          </div>
        </div>

        <div className="border-t border-emerald-400/10 p-5 lg:col-span-3 lg:border-l lg:border-t-0">
          <div className="cx-title">
            <Database className="h-3.5 w-3.5" /> Fontes integradas
          </div>
          <ul className="mt-4 space-y-3">
            {sources.map((s) => (
              <li key={s.name} className="flex items-start gap-2.5">
                <span
                  className={cn(
                    "cx-dot mt-1.5 shrink-0",
                    s.state === "on"
                      ? "cx-dot-on"
                      : s.state === "warn"
                        ? "cx-dot-warn"
                        : "cx-dot-off",
                  )}
                />
                <div className="min-w-0">
                  <div
                    className={cn(
                      "text-[13px] font-medium leading-tight",
                      s.state === "off" ? "text-slate-400" : "text-slate-100",
                    )}
                  >
                    {s.name}
                  </div>
                  <div
                    className={cn(
                      "text-[11px]",
                      s.state === "on" ? "text-emerald-400/80" : "text-slate-500",
                    )}
                  >
                    {s.detail}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Estados

function IdleState({ onDemo }: { onDemo: () => void }) {
  return (
    <section
      className="cx-card cx-fade flex flex-col items-center gap-4 px-6 py-12 text-center"
      style={{ animationDelay: "140ms" }}
    >
      <div className="relative grid h-20 w-20 place-items-center rounded-full border border-emerald-400/25">
        <div className="cx-radar absolute inset-1 rounded-full" />
        <UserSearch className="relative h-8 w-8 text-emerald-300" />
      </div>
      <div>
        <div className="text-base font-semibold text-slate-100">Aguardando consulta</div>
        <p className="mt-1 max-w-md text-sm text-slate-400">
          Informe o CPF, a finalidade e clique em CONSULTAR. Os resultados aparecem aqui,
          organizados por categoria.
        </p>
      </div>
      <button
        type="button"
        onClick={onDemo}
        className="cx-ghost flex h-9 items-center gap-2 px-4 text-xs font-semibold uppercase tracking-[0.12em]"
      >
        <Eye className="h-3.5 w-3.5" /> Ver demonstração (dados fictícios)
      </button>
    </section>
  );
}

function LoadingState() {
  const [secs, setSecs] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setSecs((s) => s + 1), 1000);
    return () => clearInterval(t);
  }, []);
  return (
    <section className="cx-card cx-fade flex flex-col items-center gap-5 px-6 py-12 text-center">
      <div className="relative grid h-24 w-24 place-items-center rounded-2xl border border-emerald-400/30 bg-emerald-400/5">
        <Fingerprint className="h-16 w-16" />
      </div>
      <div className="flex h-8 items-end gap-1">
        {Array.from({ length: 12 }).map((_, i) => (
          <span
            key={i}
            className="cx-bar block h-full w-1.5 origin-bottom rounded-full bg-emerald-400"
            style={{ animationDelay: `${i * 0.09}s` }}
          />
        ))}
      </div>
      <div>
        <div className="cx-mono text-sm font-semibold uppercase tracking-[0.2em] text-emerald-300">
          Consultando fonte autorizada
        </div>
        <p className="mt-1 text-xs text-slate-400">
          {secs}s · pode levar até 2 minutos (certidões e PDF demoram mais). Não feche a tela.
        </p>
      </div>
    </section>
  );
}

function ErrorState({ message, onClose }: { message: string; onClose: () => void }) {
  return (
    <section className="cx-card cx-fade flex flex-col items-center gap-3 border-rose-500/30 px-6 py-10 text-center">
      <div className="grid h-14 w-14 place-items-center rounded-full border border-rose-500/40 bg-rose-500/10">
        <AlertTriangle className="h-6 w-6 text-rose-400" />
      </div>
      <div className="text-base font-semibold text-slate-100">
        Não foi possível concluir a consulta
      </div>
      <p className="max-w-lg text-sm text-rose-300/90">{message}</p>
      <button
        type="button"
        onClick={onClose}
        className="cx-ghost mt-1 h-8 px-4 text-xs font-semibold"
      >
        Fechar
      </button>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Resultados

function Results({
  consultation: c,
  demo,
  onClose,
}: {
  consultation?: Consultation;
  demo?: boolean;
  onClose: () => void;
}) {
  const [tab, setTab] = useState<ResultTab>("geral");
  const [rawOpen, setRawOpen] = useState(false);
  const [printing, setPrinting] = useState(false);
  const donePrinting = useCallback(() => setPrinting(false), []);
  const ok = demo || c?.status === "sucesso";
  const data = c ? extractData(c.result) : null;
  const publicData = c ? publicDataOf(c.result) : null;
  const legal = c ? legalOf(c.result) : null;
  const profile: Profile = useMemo(
    () => (demo || !c ? DEMO_PROFILE : consultationProfile(c, legal)),
    [demo, c, legal],
  );
  const pdfHref = c?.pdfUrl
    ? c.pdfUrl.startsWith("/")
      ? resolveGpsDriveUrl(c.pdfUrl)
      : c.pdfUrl
    : null;
  const empty =
    !demo &&
    (data == null ||
      (typeof data === "object" &&
        !Array.isArray(data) &&
        Object.keys(data as object).length === 0));

  const tabs: {
    key: ResultTab;
    label: string;
    list?: ProfileList;
    count?: number;
    icon: LucideIcon;
  }[] = (
    [
      { key: "geral", label: "Visão geral", icon: Eye },
      { key: "enderecos", label: "Endereços", list: "addresses", icon: MapPin },
      { key: "antecedentes", label: "Antecedentes", list: "records", icon: ShieldAlert },
      { key: "processos", label: "Processos", list: "processes", icon: Gavel },
      { key: "veiculos", label: "Veículos", list: "vehicles", icon: Car },
      { key: "telefones", label: "Telefones", list: "phones", icon: Phone },
      { key: "emails", label: "E-mails", list: "emails", icon: Mail },
      { key: "vinculos", label: "Vínculos", list: "links", icon: Network },
      { key: "publicos", label: "Dados públicos", icon: Landmark },
    ] as { key: ResultTab; label: string; list?: ProfileList; icon: LucideIcon }[]
  ).map((t) => ({
    ...t,
    count:
      t.key === "publicos"
        ? publicDataCount(publicData)
        : t.list && !profile.notCovered?.[t.list]
          ? profile[t.list].length
          : undefined,
  }));

  const reportCpf = maskCpf(profile.cpf ?? c?.document ?? "");
  return (
    <section className="cx-fade space-y-4">
      <ConsultationPrintReport
        open={printing}
        onDone={donePrinting}
        fileName={`Consulta ${profile.name ?? ""} ${reportCpf}`.replace(/\s+/g, " ").trim()}
        meta={{
          id: c?.id,
          typeLabel: demo ? "Demonstração" : typeLabel(c!.type),
          createdAt: c?.createdAt,
          userName: c?.userName,
          purpose: c?.purpose,
          cpf: reportCpf,
          demo,
        }}
        profile={profile}
        publicData={demo ? null : publicData}
        legal={demo ? null : legal}
      />
      {/* Barra do resultado */}
      <div className="cx-card flex flex-wrap items-center justify-between gap-3 px-4 py-3">
        <div className="flex min-w-0 items-center gap-3">
          {ok ? (
            <CheckCircle2 className="h-5 w-5 shrink-0 text-emerald-400" />
          ) : (
            <AlertTriangle className="h-5 w-5 shrink-0 text-rose-400" />
          )}
          <div className="min-w-0">
            <div className="truncate text-sm font-semibold text-slate-100">
              {demo ? "Demonstração" : typeLabel(c!.type)} ·{" "}
              <span className="cx-mono">{maskCpf(profile.cpf ?? "")}</span>
            </div>
            <div className="truncate text-[11px] text-slate-400">
              {demo
                ? "Perfil fictício para visualizar o layout — nenhum dado real."
                : `${fmtDate(c!.createdAt)}${c!.userName ? ` · por ${c!.userName}` : ""}${c!.purpose ? ` · finalidade: ${c!.purpose}` : ""}`}
            </div>
          </div>
          {demo && (
            <span className="shrink-0 rounded-md border border-amber-400/40 bg-amber-400/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.14em] text-amber-300">
              Dados fictícios
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {/* Relatório GPS Drive com tudo (2026-10-12) — antes abria o PDF
              do fornecedor; a certidão oficial continua em Antecedentes. */}
          {ok && !empty && (
            <button
              type="button"
              onClick={() => setPrinting(true)}
              disabled={printing}
              className="cx-btn flex h-8 items-center gap-1.5 px-3 text-[11px]"
            >
              <FileText className="h-3.5 w-3.5" /> BAIXAR PDF
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            aria-label="Fechar"
            className="cx-ghost grid h-8 w-8 place-items-center"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      {!ok ? (
        <ErrorState message={c?.message ?? "A consulta não retornou dados."} onClose={onClose} />
      ) : empty ? (
        <div className="cx-card px-6 py-10 text-center text-sm text-slate-400">
          Nenhum dado encontrado para este CPF.
        </div>
      ) : (
        <>
          <div className="cx-card cx-scroll flex gap-1 overflow-x-auto px-2">
            {tabs.map((t) => (
              <button
                key={t.key}
                type="button"
                data-active={tab === t.key}
                onClick={() => setTab(t.key)}
                className="cx-tab flex shrink-0 items-center gap-1.5 px-3 py-3 text-[11px] font-bold uppercase tracking-[0.12em]"
              >
                <t.icon className="h-3.5 w-3.5" />
                {t.label}
                {t.count != null && (
                  <span
                    className={cn(
                      "rounded px-1.5 py-px text-[10px]",
                      t.count
                        ? "bg-emerald-400/15 text-emerald-300"
                        : "bg-slate-700/40 text-slate-500",
                    )}
                  >
                    {t.count}
                  </span>
                )}
              </button>
            ))}
          </div>

          <div key={tab} className="cx-fade">
            {tab === "geral" && (
              <Overview
                profile={profile}
                onTab={setTab}
                publicData={demo ? undefined : publicData}
              />
            )}
            {tab === "publicos" && <PublicDataPanel data={publicData} />}
            {tab === "enderecos" && <AddressesPanel profile={profile} full />}
            {tab === "antecedentes" && <RecordsPanel profile={profile} full pdfHref={pdfHref} />}
            {tab === "processos" && (
              <div className="space-y-4">
                <ProcessesPanel profile={profile} full />
                {!demo && (
                  <LegalChecksPanel
                    data={legal}
                    renderData={(v) => <DataView value={v} depth={0} />}
                  />
                )}
              </div>
            )}
            {tab === "veiculos" && <VehiclesPanel profile={profile} full />}
            {tab === "telefones" && <PhonesPanel profile={profile} full />}
            {tab === "emails" && <EmailsPanel profile={profile} full />}
            {tab === "vinculos" && <LinksPanel profile={profile} full />}
          </div>

          {!demo && data != null && (
            <div className="cx-card">
              <button
                type="button"
                onClick={() => setRawOpen((o) => !o)}
                className="flex w-full items-center justify-between px-4 py-3"
              >
                <span className="cx-title">
                  <Database className="h-3.5 w-3.5" /> Dados completos da fonte
                </span>
                <ChevronDown
                  className={cn(
                    "h-4 w-4 text-emerald-300 transition-transform",
                    rawOpen && "rotate-180",
                  )}
                />
              </button>
              {rawOpen && (
                <div className="border-t border-emerald-400/10 p-4 text-slate-200">
                  <DataView value={data} depth={0} />
                </div>
              )}
            </div>
          )}
        </>
      )}
    </section>
  );
}

function Overview({
  profile: p,
  onTab,
  publicData,
}: {
  profile: Profile;
  onTab: (t: ResultTab) => void;
  // undefined = demonstração (sem painel); null = consulta antiga, sem dados públicos.
  publicData?: ReturnType<typeof publicDataOf>;
}) {
  return (
    <div className="grid gap-4 lg:grid-cols-12">
      <PersonalCard profile={p} className="lg:col-span-4" />
      <LocationCard profile={p} className="lg:col-span-5" />
      <AddressesPanel
        profile={p}
        className="lg:col-span-3"
        limit={3}
        onMore={() => onTab("enderecos")}
      />
      <RecordsPanel
        profile={p}
        className="md:col-span-1 lg:col-span-4"
        limit={2}
        onMore={() => onTab("antecedentes")}
      />
      <ProcessesPanel
        profile={p}
        className="lg:col-span-4"
        limit={2}
        onMore={() => onTab("processos")}
      />
      <VehiclesPanel
        profile={p}
        className="lg:col-span-4"
        limit={2}
        onMore={() => onTab("veiculos")}
      />
      <PhonesPanel
        profile={p}
        className="lg:col-span-4"
        limit={3}
        onMore={() => onTab("telefones")}
      />
      <EmailsPanel profile={p} className="lg:col-span-4" limit={3} onMore={() => onTab("emails")} />
      <LinksPanel
        profile={p}
        className="lg:col-span-4"
        limit={3}
        onMore={() => onTab("vinculos")}
      />
      {publicData !== undefined && (
        <PublicDataSummary
          data={publicData}
          className="lg:col-span-12"
          onOpen={() => onTab("publicos")}
        />
      )}
    </div>
  );
}

function Panel({
  title,
  icon: Icon,
  count,
  className,
  children,
  onMore,
  hidden,
}: {
  title: string;
  icon: LucideIcon;
  count?: number;
  className?: string;
  children: ReactNode;
  onMore?: () => void;
  hidden?: number;
}) {
  return (
    <div className={cn("cx-card flex flex-col p-4", className)}>
      <div className="mb-3 flex items-center justify-between gap-2">
        <div className="cx-title">
          <Icon className="h-3.5 w-3.5" /> {title}
        </div>
        {count != null && (
          <span className="cx-mono text-[11px] text-slate-500">
            {count.toString().padStart(2, "0")}
          </span>
        )}
      </div>
      <div className="flex-1">{children}</div>
      {onMore && !!hidden && hidden > 0 && (
        <button
          type="button"
          onClick={onMore}
          className="mt-3 self-start text-[11px] font-semibold uppercase tracking-[0.12em] text-emerald-300 hover:text-emerald-200"
        >
          Ver mais {hidden} →
        </button>
      )}
    </div>
  );
}

function Empty({
  text = "Nenhum registro encontrado na fonte para este CPF.",
  profile,
  list,
}: {
  text?: string;
  profile?: Profile;
  list?: ProfileList;
}) {
  if (profile && list && profile.notCovered?.[list]) text = "Não incluído neste tipo de consulta.";
  return (
    <div className="flex h-full min-h-[64px] items-center justify-center rounded-lg border border-dashed border-slate-700/60 px-3 text-center text-xs text-slate-500">
      {text}
    </div>
  );
}

function Field({ label, value, mono }: { label: string; value?: string; mono?: boolean }) {
  return (
    <div className="min-w-0">
      <dt className="cx-label">{label}</dt>
      <dd
        className={cn(
          "mt-0.5 truncate text-[13px] font-medium",
          value ? "text-slate-100" : "text-slate-600",
          mono && "cx-mono",
        )}
        title={value}
      >
        {value ?? "—"}
      </dd>
    </div>
  );
}

function PersonalCard({ profile: p, className }: { profile: Profile; className?: string }) {
  const regular = p.status && /regular|ativ/i.test(p.status);
  return (
    <div className={cn("cx-card p-4", className)}>
      <div className="cx-title mb-4">
        <User className="h-3.5 w-3.5" /> Informações pessoais
      </div>
      <div className="flex gap-4">
        <div className="relative h-28 w-24 shrink-0 overflow-hidden rounded-xl border border-emerald-400/30 bg-gradient-to-b from-slate-800/60 to-slate-900/80">
          {p.photo ? (
            <img src={p.photo} alt="" className="h-full w-full object-cover" />
          ) : (
            <div className="flex h-full flex-col items-center justify-center gap-1 text-slate-600">
              <User className="h-10 w-10" />
              <span className="text-[9px] uppercase tracking-wider">Sem foto</span>
            </div>
          )}
          <span className="absolute left-1 top-1 h-2.5 w-2.5 border-l-2 border-t-2 border-emerald-300/80" />
          <span className="absolute bottom-1 right-1 h-2.5 w-2.5 border-b-2 border-r-2 border-emerald-300/80" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="cx-label">Nome completo</div>
          <div className="mt-0.5 text-[15px] font-bold uppercase leading-snug text-white">
            {p.name ?? "—"}
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <span className="cx-mono rounded-md border border-cyan-400/30 bg-cyan-400/10 px-2 py-0.5 text-xs text-cyan-200">
              {p.cpf ? maskCpf(p.cpf) : "—"}
            </span>
            {p.status && (
              <span
                className={cn(
                  "rounded-md border px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.1em]",
                  regular
                    ? "border-emerald-400/40 bg-emerald-400/10 text-emerald-300"
                    : "border-amber-400/40 bg-amber-400/10 text-amber-300",
                )}
              >
                {p.status}
              </span>
            )}
          </div>
        </div>
      </div>
      <dl className="mt-4 grid grid-cols-2 gap-x-4 gap-y-3 border-t border-emerald-400/10 pt-4">
        <Field label="RG" value={p.rg} mono />
        <Field label="Nascimento" value={p.birthDate} mono />
        <Field label="Sexo" value={p.gender} />
        <Field label="Nacionalidade" value={p.nationality} />
        <Field label="Naturalidade" value={p.birthplace} />
        <div className="col-span-2">
          <Field label="Nome da mãe" value={p.mother} />
        </div>
        <div className="col-span-2">
          <Field label="Nome do pai" value={p.father} />
        </div>
        <div className="col-span-2">
          <Field label="Situação cadastral" value={p.status} />
        </div>
        {p.extras.map((x) => (
          <div key={x.label} className={cn(x.value.length > 22 && "col-span-2")}>
            <Field label={x.label} value={x.value} mono={/^[\d./-]+$/.test(x.value)} />
          </div>
        ))}
      </dl>
    </div>
  );
}

function LocationCard({ profile: p, className }: { profile: Profile; className?: string }) {
  const main = p.addresses[0];
  const q = p.coords
    ? `${p.coords.lat},${p.coords.lng}`
    : main
      ? [main.line, main.district, main.city, main.zip].filter(Boolean).join(", ")
      : null;
  const href = q
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}`
    : null;
  return (
    <div className={cn("cx-card flex flex-col overflow-hidden", className)}>
      <div className="flex items-center justify-between px-4 pt-4">
        <div className="cx-title">
          <MapPin className="h-3.5 w-3.5" /> Localização
        </div>
        {main?.kind && (
          <span className="text-[10px] font-bold uppercase tracking-[0.12em] text-emerald-300/70">
            {main.kind}
          </span>
        )}
      </div>
      <div className="cx-map relative mx-4 mt-3 min-h-[230px] flex-1 overflow-hidden rounded-xl border border-emerald-400/20 bg-[#06101e]">
        {q ? (
          <>
            <iframe
              title="Mapa"
              src={`https://www.google.com/maps?q=${encodeURIComponent(q)}&z=15&output=embed`}
              className="absolute inset-0 h-full w-full border-0"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
            <div className="pointer-events-none absolute inset-0 shadow-[inset_0_0_60px_rgba(3,7,18,.9)]" />
            <div className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
              <span className="block h-16 w-16 animate-ping rounded-full border border-emerald-400/50" />
            </div>
          </>
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-slate-500">
            <div
              className="absolute inset-0 opacity-40"
              style={{
                backgroundImage:
                  "linear-gradient(rgba(34,211,238,.08) 1px,transparent 1px),linear-gradient(90deg,rgba(34,211,238,.08) 1px,transparent 1px)",
                backgroundSize: "22px 22px",
              }}
            />
            <MapPin className="relative h-7 w-7 text-emerald-400/50" />
            <span className="relative text-xs">Sem endereço para exibir no mapa</span>
          </div>
        )}
      </div>
      <div className="flex flex-col gap-3 p-4 sm:flex-row sm:items-end sm:justify-between">
        <div className="min-w-0">
          <div className="cx-label">Endereço principal</div>
          <div className="mt-0.5 text-[13px] font-medium text-slate-100">
            {main ? main.line : "—"}
          </div>
          {main && (
            <div className="text-xs text-slate-400">
              {[main.district, main.city, main.zip && `CEP ${main.zip}`]
                .filter(Boolean)
                .join(" · ")}
            </div>
          )}
          <div className="cx-mono mt-1 text-[11px] text-cyan-300/70">
            {p.coords
              ? `${p.coords.lat.toFixed(5)}, ${p.coords.lng.toFixed(5)}`
              : "Coordenadas não informadas pela fonte"}
          </div>
        </div>
        {href && (
          <a
            href={href}
            target="_blank"
            rel="noreferrer"
            className="cx-ghost flex h-9 shrink-0 items-center gap-1.5 px-3 text-xs font-semibold"
          >
            <Navigation className="h-3.5 w-3.5" /> Abrir localização
          </a>
        )}
      </div>
    </div>
  );
}

type PanelProps = {
  profile: Profile;
  className?: string;
  limit?: number;
  full?: boolean;
  onMore?: () => void;
};
const slice = <T,>(arr: T[], limit?: number) => (limit ? arr.slice(0, limit) : arr);

function Tag({
  children,
  tone = "slate",
}: {
  children: ReactNode;
  tone?: "slate" | "green" | "cyan" | "amber" | "rose";
}) {
  const tones = {
    slate: "border-slate-600/40 text-slate-400",
    green: "border-emerald-400/40 bg-emerald-400/10 text-emerald-300",
    cyan: "border-cyan-400/40 bg-cyan-400/10 text-cyan-300",
    amber: "border-amber-400/40 bg-amber-400/10 text-amber-300",
    rose: "border-rose-400/40 bg-rose-400/10 text-rose-300",
  };
  return (
    <span
      className={cn(
        "rounded border px-1.5 py-px text-[10px] font-semibold uppercase tracking-[0.08em]",
        tones[tone],
      )}
    >
      {children}
    </span>
  );
}

function AddressesPanel({ profile: p, className, limit, full, onMore }: PanelProps) {
  return (
    <Panel
      title="Endereços encontrados"
      icon={Home}
      count={p.addresses.length}
      className={className}
      onMore={onMore}
      hidden={p.addresses.length - (limit ?? 0)}
    >
      {!p.addresses.length ? (
        <Empty profile={p} list="addresses" />
      ) : (
        <ol className={cn("space-y-2", full && "grid gap-2 space-y-0 md:grid-cols-2")}>
          {slice(p.addresses, limit).map((a, i) => (
            <li key={i} className="cx-row flex gap-3 p-3">
              <span
                className={cn(
                  "cx-mono grid h-6 w-6 shrink-0 place-items-center rounded-md text-[11px] font-bold",
                  i === 0 ? "bg-emerald-400/20 text-emerald-300" : "bg-slate-700/40 text-slate-400",
                )}
              >
                {i + 1}
              </span>
              <div className="min-w-0 flex-1">
                <div className="text-[13px] font-medium leading-snug text-slate-100">{a.line}</div>
                <div className="text-xs text-slate-400">
                  {[a.district, a.city, a.zip && `CEP ${a.zip}`].filter(Boolean).join(" · ") || "—"}
                </div>
                <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                  {a.kind && <Tag tone={/comerc/i.test(a.kind) ? "cyan" : "green"}>{a.kind}</Tag>}
                  {a.date && (
                    <span className="flex items-center gap-1 text-[10px] text-slate-500">
                      <Clock className="h-3 w-3" /> {a.date}
                    </span>
                  )}
                  {a.source && <span className="text-[10px] text-slate-500">· {a.source}</span>}
                </div>
              </div>
            </li>
          ))}
        </ol>
      )}
    </Panel>
  );
}

function RecordsPanel({
  profile: p,
  className,
  limit,
  onMore,
  pdfHref,
}: PanelProps & { pdfHref?: string | null }) {
  return (
    <Panel
      title="Registros e antecedentes"
      icon={ShieldAlert}
      count={p.records.length}
      className={className}
      onMore={onMore}
      hidden={p.records.length - (limit ?? 0)}
    >
      {!p.records.length ? (
        <Empty text="Sem certidão nesta consulta. Use “Antecedentes criminais” para emitir pela fonte oficial." />
      ) : (
        <ul className="space-y-2">
          {slice(p.records, limit).map((r, i) => (
            <li key={i} className="cx-row p-3">
              <div className="flex items-start justify-between gap-2">
                <div className="text-[13px] font-medium text-slate-100">{r.title}</div>
                <Tag
                  tone={
                    r.status === "nada_consta" ? "green" : r.status === "consta" ? "rose" : "slate"
                  }
                >
                  {r.status === "nada_consta"
                    ? "Nada consta"
                    : r.status === "consta"
                      ? "Consta"
                      : "Info"}
                </Tag>
              </div>
              {r.detail && <p className="mt-1 text-xs text-slate-400">{r.detail}</p>}
              <div className="mt-1.5 flex items-center justify-between gap-2">
                {r.source && <span className="text-[10px] text-slate-500">Fonte: {r.source}</span>}
                {pdfHref && !r.noPdf && (
                  <a
                    href={pdfHref}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center gap-1 text-[11px] font-semibold text-emerald-300 hover:text-emerald-200"
                  >
                    Certidão PDF <ExternalLink className="h-3 w-3" />
                  </a>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

function ProcessesPanel({ profile: p, className, limit, onMore }: PanelProps) {
  return (
    <Panel
      title="Processos judiciais"
      icon={Gavel}
      count={p.processes.length}
      className={className}
      onMore={onMore}
      hidden={p.processes.length - (limit ?? 0)}
    >
      {!p.processes.length ? (
        <Empty profile={p} list="processes" />
      ) : (
        <ul className="space-y-2">
          {slice(p.processes, limit).map((r, i) => (
            <li key={i} className="cx-row p-3">
              <div className="cx-mono text-[12px] text-cyan-200">{r.number}</div>
              <div className="mt-0.5 text-[13px] text-slate-100">{r.subject ?? "—"}</div>
              <div className="mt-1 flex flex-wrap items-center gap-1.5 text-[11px] text-slate-400">
                {r.court && <span>{r.court}</span>}
                {r.date && <span>· {r.date}</span>}
                {r.status && <Tag>{r.status}</Tag>}
              </div>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

function VehiclesPanel({ profile: p, className, limit, onMore }: PanelProps) {
  return (
    <Panel
      title="Veículos vinculados"
      icon={Car}
      count={p.vehicles.length}
      className={className}
      onMore={onMore}
      hidden={p.vehicles.length - (limit ?? 0)}
    >
      {!p.vehicles.length ? (
        <Empty profile={p} list="vehicles" />
      ) : (
        <ul className="space-y-2">
          {slice(p.vehicles, limit).map((v, i) => (
            <li key={i} className="cx-row flex items-center gap-3 p-3">
              <LicensePlate plate={v.plate} size="md" />
              <div className="min-w-0">
                <div className="truncate text-[13px] text-slate-100">{v.model ?? "—"}</div>
                <div className="text-[11px] text-slate-400">
                  {[v.year, v.color, v.source].filter(Boolean).join(" · ")}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

function PhonesPanel({ profile: p, className, limit, onMore }: PanelProps) {
  return (
    <Panel
      title="Telefones encontrados"
      icon={Phone}
      count={p.phones.length}
      className={className}
      onMore={onMore}
      hidden={p.phones.length - (limit ?? 0)}
    >
      {!p.phones.length ? (
        <Empty profile={p} list="phones" />
      ) : (
        <ul className="space-y-2">
          {slice(p.phones, limit).map((ph, i) => (
            <li key={i} className="cx-row flex items-center justify-between gap-3 p-3">
              <div className="min-w-0">
                <div className="cx-mono text-[13px] text-slate-100">{ph.number}</div>
                <div className="text-[11px] text-slate-400">
                  {[ph.carrier, ph.source].filter(Boolean).join(" · ") || "—"}
                </div>
              </div>
              {ph.kind && <Tag tone="cyan">{ph.kind}</Tag>}
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

function EmailsPanel({ profile: p, className, limit, onMore }: PanelProps) {
  return (
    <Panel
      title="E-mails"
      icon={Mail}
      count={p.emails.length}
      className={className}
      onMore={onMore}
      hidden={p.emails.length - (limit ?? 0)}
    >
      {!p.emails.length ? (
        <Empty profile={p} list="emails" />
      ) : (
        <ul className="space-y-2">
          {slice(p.emails, limit).map((e, i) => (
            <li key={i} className="cx-row p-3">
              <div className="truncate text-[13px] text-slate-100">{e.address}</div>
              {e.source && <div className="text-[11px] text-slate-500">{e.source}</div>}
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

function LinksPanel({ profile: p, className, limit, onMore }: PanelProps) {
  return (
    <Panel
      title="Vínculos e relacionamentos"
      icon={Users}
      count={p.links.length}
      className={className}
      onMore={onMore}
      hidden={p.links.length - (limit ?? 0)}
    >
      {!p.links.length ? (
        <Empty profile={p} list="links" />
      ) : (
        <ul className="space-y-2">
          {slice(p.links, limit).map((l, i) => (
            <li key={i} className="cx-row flex items-center gap-3 p-3">
              <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full border border-emerald-400/30 bg-emerald-400/10 text-[11px] font-bold text-emerald-300">
                {l.name
                  .split(/\s+/)
                  .slice(0, 2)
                  .map((s) => s[0])
                  .join("")}
              </span>
              <div className="min-w-0 flex-1">
                <div className="truncate text-[13px] text-slate-100">{l.name}</div>
                {l.document && (
                  <div className="cx-mono text-[11px] text-slate-500">{l.document}</div>
                )}
              </div>
              {l.relation && <Tag tone="green">{l.relation}</Tag>}
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

// ---------------------------------------------------------------------------
// Dados brutos (genérico) — tudo que a fonte devolveu, mesmo o não mapeado.

// Mostra só `dados` (e o resto se não houver `dados`).
function humanize(key: string): string {
  const s = key
    .replace(/[_-]+/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .trim();
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
}
const isPlain = (v: unknown) => v == null || typeof v !== "object";
function plainText(v: unknown): string {
  if (v == null || v === "") return "—";
  if (typeof v === "boolean") return v ? "Sim" : "Não";
  return String(v);
}

function DataView({ value, depth }: { value: unknown; depth: number }) {
  if (isPlain(value)) {
    const t = plainText(value);
    return /^https?:\/\//.test(t) ? (
      <a
        href={t}
        target="_blank"
        rel="noreferrer"
        className="inline-flex items-center gap-1 break-all text-emerald-300 hover:underline"
      >
        {t} <ExternalLink className="h-3 w-3" />
      </a>
    ) : (
      <span className="whitespace-pre-wrap break-words">{t}</span>
    );
  }
  if (Array.isArray(value)) {
    if (!value.length) return <span className="text-slate-500">—</span>;
    if (value.every(isPlain)) return <span>{value.map(plainText).join(", ")}</span>;
    const objs = value.filter(
      (v): v is Record<string, unknown> => !!v && typeof v === "object" && !Array.isArray(v),
    );
    const cols = [...new Set(objs.flatMap((o) => Object.keys(o)))];
    if (
      objs.length === value.length &&
      objs.every((o) => Object.values(o).every(isPlain)) &&
      cols.length <= 8
    ) {
      return (
        <div className="cx-scroll overflow-x-auto rounded-lg border border-emerald-400/15">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-emerald-400/15 bg-emerald-400/5 text-left">
                {cols.map((c) => (
                  <th key={c} className="cx-label px-2 py-1.5">
                    {humanize(c)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700/40">
              {objs.map((o, i) => (
                <tr key={i}>
                  {cols.map((c) => (
                    <td key={c} className="px-2 py-1.5 align-top">
                      {plainText(o[c])}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }
    return (
      <div className="space-y-2">
        {value.map((v, i) => (
          <div key={i} className="rounded-lg border border-slate-700/50 p-2">
            <DataView value={v} depth={depth + 1} />
          </div>
        ))}
      </div>
    );
  }
  const entries = Object.entries(value as Record<string, unknown>);
  const plain = entries.filter(([, v]) => isPlain(v));
  const nested = entries.filter(([, v]) => !isPlain(v));
  return (
    <div className="space-y-3">
      {plain.length > 0 && (
        <dl className="grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2 lg:grid-cols-3">
          {plain.map(([k, v]) => (
            <div key={k} className="min-w-0">
              <dt className="cx-label">{humanize(k)}</dt>
              <dd className="font-medium">
                <DataView value={v} depth={depth + 1} />
              </dd>
            </div>
          ))}
        </dl>
      )}
      {nested.map(([k, v]) => (
        <div key={k} className={cn(depth === 0 && "rounded-lg border border-slate-700/50 p-3")}>
          <div className="cx-label mb-2 text-emerald-300/80">{humanize(k)}</div>
          <DataView value={v} depth={depth + 1} />
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Histórico (auditoria)

function History({ onOpen }: { onOpen: (id: string) => void }) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState("");
  const doc = search.replace(/\D/g, "");
  const { data, isLoading } = useConsultationHistory(page, pageSize, doc);
  const total = data?.total ?? 0;
  return (
    <section className="cx-card p-4 md:p-5">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="cx-title">
            <Clock className="h-3.5 w-3.5" /> Histórico de consultas
          </div>
          <p className="mt-1 text-xs text-slate-500">
            Registro de auditoria: quem consultou, quando e para quê. Reabrir não gasta crédito.
          </p>
        </div>
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-emerald-400/60" />
          <input
            value={search}
            onChange={(e) => {
              setSearch(maskCpf(e.target.value));
              setPage(1);
            }}
            placeholder="Buscar CPF…"
            className="cx-input cx-mono h-9 w-52 pl-8 pr-2 text-sm"
          />
        </div>
      </div>
      {isLoading ? (
        <div className="flex items-center gap-2 py-6 text-sm text-slate-400">
          <Loader2 className="h-4 w-4 animate-spin text-emerald-400" /> Carregando…
        </div>
      ) : !data?.items.length ? (
        <div className="py-8 text-center text-sm text-slate-500">Nenhuma consulta feita ainda.</div>
      ) : (
        <>
          <div className="cx-scroll overflow-x-auto">
            <table className="w-full min-w-[720px] text-sm">
              <thead>
                <tr className="border-b border-emerald-400/15 text-left">
                  {["Data", "Consulta", "CPF", "Por", "Finalidade", "Resultado", ""].map((h) => (
                    <th key={h} className="cx-label px-2 py-2">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800">
                {data.items.map((c) => (
                  <tr key={c.id} className="transition-colors hover:bg-emerald-400/[0.04]">
                    <td className="cx-mono px-2 py-2.5 text-xs text-slate-300">
                      {fmtDate(c.createdAt)}
                    </td>
                    <td className="px-2 py-2.5 text-xs font-medium text-slate-100">
                      {typeLabel(c.type)}
                    </td>
                    <td className="cx-mono px-2 py-2.5 text-xs text-cyan-200">
                      {maskCpf(c.document)}
                    </td>
                    <td className="px-2 py-2.5 text-xs text-slate-300">{c.userName ?? "—"}</td>
                    <td
                      className="max-w-[220px] truncate px-2 py-2.5 text-xs text-slate-400"
                      title={c.purpose ?? undefined}
                    >
                      {c.purpose ?? "—"}
                    </td>
                    <td className="px-2 py-2.5">
                      <span className="flex items-center gap-1.5">
                        <Tag tone={c.status === "sucesso" ? "green" : "rose"}>
                          {c.status === "sucesso" ? "OK" : "Erro"}
                        </Tag>
                        {c.pdfUrl && <FileText className="h-3.5 w-3.5 text-slate-500" />}
                      </span>
                    </td>
                    <td className="px-2 py-2.5 text-right">
                      <button
                        type="button"
                        onClick={() => onOpen(c.id)}
                        className="cx-ghost h-7 px-3 text-[11px] font-semibold"
                      >
                        Ver
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="cx-pager mt-2">
            <ListPagination
              page={page}
              pageCount={Math.max(1, Math.ceil(total / pageSize))}
              pageSize={pageSize}
              total={total}
              onPage={setPage}
              onPageSize={(n) => {
                setPageSize(n);
                setPage(1);
              }}
            />
          </div>
        </>
      )}
    </section>
  );
}
